import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import { bpStartSearchComment, useGetCommentsDeeplink } from '@vimeo/comments-view';
import useOnClickOutside from '@vimeo/ui/hooks/use-outside-click/useOnClickOutside';
import { useViewer } from '@vimeo/viewer';

import { COMMENTS_ANALYTICS_PROPS, CommentsModuleProps } from '../utils/types';

/**
 * Hook to manage the comments module state and behavior.
 * @param clipRequestId - The request ID for the clip.
 * @param clipId - The ID of the clip.
 * @param privacyView - The privacy view of the clip.
 * @param showComments - Flag indicating whether to show comments.
 * @returns An object containing functions and state related to the comments module.
 */
export const useCommentsModule = ({
  clipRequestId,
  clipId,
  privacyView,
  showComments,
}: CommentsModuleProps) => {
  const viewer = useViewer();

  const { setActiveMobileDrawer } = useLayout();
  const [showCommentSearchBar, setShowCommentSearchBar] = useState(false);
  const [searchCommentQuery, setSearchCommentQuery] = useState('');
  const searchInputRef = useRef<HTMLDivElement>(null);

  const onCloseSearch = useCallback(() => {
    setSearchCommentQuery('');
    setShowCommentSearchBar(false);
  }, [setSearchCommentQuery, setShowCommentSearchBar]);

  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((search: string) => {
        setSearchCommentQuery(search);
      }, 500),
    [],
  );

  const updateCommentSearchQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const search = e.target.value.trim();
      debouncedSetSearchQuery(search);
    },
    [debouncedSetSearchQuery],
  );

  useOnClickOutside(
    [searchInputRef],
    () => {
      if (showCommentSearchBar && !searchCommentQuery) {
        onCloseSearch();
      }
    },
    null,
    [searchCommentQuery, showCommentSearchBar],
  );

  const { deeplinkNoteId } = useGetCommentsDeeplink(clipRequestId, true);

  // This makes sure to automatically open the comments drawer for deep linked comments on mobile
  useEffect(() => {
    if (!showComments) return;
    if (deeplinkNoteId) {
      setActiveMobileDrawer('COMMENTS');
    }
  }, [setActiveMobileDrawer, showComments, deeplinkNoteId]);

  const onSearchClick = useCallback(() => {
    bpStartSearchComment({
      isInternal: false,
      videoId: parseFloat(clipId),
      videoPrivacy: privacyView,
      viewer,
      analyticsProps: COMMENTS_ANALYTICS_PROPS,
    });
    setShowCommentSearchBar(true);
  }, [clipId, privacyView, viewer]);

  return {
    onCloseSearch,
    onSearchClick,
    searchCommentQuery,
    searchInputRef,
    showCommentSearchBar,
    updateCommentSearchQuery,
  };
};
